'use client';

import React from 'react';
import { mergeProps, useFocusRing, useHover, usePress } from 'react-aria';

interface SlotProps {
  children?: React.ReactNode[];
  isDisabled?: boolean | undefined;
}

interface ButtonDataAttributes {
  'data-hovered'?: boolean | null;
  'data-focus-visible'?: boolean | null;
  'data-pressed'?: boolean | null;
  'data-disabled'?: boolean | null;
}

function setRef<TInstance>(ref: React.Ref<TInstance>, instance: TInstance) {
  if (ref instanceof Function) {
    ref(instance);
  } else if (ref != null) {
    (ref as React.MutableRefObject<TInstance>).current = instance;
  }
}

function combinedRef<TInstance>(refs: React.Ref<TInstance>[]) {
  return (instance: TInstance | null) => refs.forEach((ref) => setRef(ref, instance));
}

export const Slot = React.forwardRef<HTMLElement, SlotProps>((props, forwardedRef) => {
  const { children, isDisabled, ...slotProps } = props;
  const { hoverProps, isHovered } = useHover(slotProps);
  const { focusProps, isFocusVisible } = useFocusRing();
  const { pressProps, isPressed } = usePress(slotProps);

  const correctChild = children && children[1];

  if (!React.isValidElement(correctChild)) {
    throw new Error('Slot component requires a valid React element as its child.');
  }

  const childRef = (correctChild as React.ReactElement & { ref: React.Ref<HTMLElement> }).ref;

  const additionalProps: ButtonDataAttributes & React.HTMLAttributes<HTMLElement> = {
    ['data-hovered']: isHovered || null,
    ['data-focus-visible']: isFocusVisible || null,
    ['data-pressed']: isPressed || null,
    ['data-disabled']: isDisabled || null
  };

  return React.cloneElement(correctChild, {
    ...mergeProps(
      slotProps,
      correctChild.props,
      hoverProps,
      focusProps,
      pressProps,
      additionalProps
    ),
    ref: combinedRef([forwardedRef, childRef])
  });
});
